import { useEffect, useMemo, useRef, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { SignatureClient } from "@/Common/Api/Signature/SignatureClient";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { PatientBannerVertical } from "@/Common/Components/Patient/PatientBannerVertical";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { SignaturePinControl } from "@/Common/Components/Sign/SignaturePinControl";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useStateRef } from "@/Common/CustomHooks/useStateRef";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PatientBannerHelper } from "@/Common/Helpers/PatientBannerHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { SecondScreenHelper } from "@/Common/Helpers/SecondScreenHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { EnumSentFaxItemType } from "@/Common/Models/Faxing/EnumSentFaxItemType";
import { FaxGenericPDFComposite } from "@/Common/Models/Faxing/FaxGenericPDFComposite";
import { FileType } from "@/Common/Models/FileType";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import { PinFrequencyDocumentType } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";
import { MedicalDocumentItem } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { IDocumentCreateEditUIModel } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { SendDocumentStrategy } from "@/Documents/Services/Portal/Strategies/SendDocumentStrategy";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileResponse } from "@/ExportableFile/Models/ExportableFileResponse";
import { MedicalSummaryPanel } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanel";
import { MedicalSummaryPanelContext } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanelEnums";
import { SummaryPanelButtons } from "@/Patient/Components/MedicalSummaryPanel/SummaryPanelButtons";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { DocumentFax } from "../DocumentFax/DocumentFax";
import { RevokeApprovalModal } from "../DocumentView/RevokeApprovalModal";
import { OutboundFaxCreationPopup } from "../OutboundFax/OutboundFaxCreationPopup";
import { PdfAnnotationEditor, PdfAnnotationEditorRef } from "./PdfAnnotationEditor";

import styles from "./DocumentInAppView.module.scss";

enum DocumentNavigation {
    Forward,
    Backward
}

interface ISignatureFlowState {
    canUserSign: boolean;
    shouldSkipPinTokenValidation: boolean;
    shouldSkipToSetActionToSignature: boolean;
}

interface IDocumentInAppViewProps {
    /** Selected document model from document store */
    document: IDocumentCreateEditUIModel;

    /** Selected document index from DocumentsTableData */
    documentIndex: number;

    /** DocumentsTableData length from document store */
    documentCount: number;

    /** Whether the send to portal popup is currently open. */
    isSendToPortalPopupOpen: boolean;

    /**
     * Sync view when changing the selected document model
     * @param navigation Determines if we are moving forward or back in the DocumentsTableData.
     */
    onDocumentNavigation?: (navigation: DocumentNavigation) => void;

    /** Selected document portal linked item from the document store */
    portalLinkedItem: IPortalLinkedItemModel;

    /** Whether the component is used from the patient file. */
    isPatientContext?: boolean;
}

function DocumentInAppView(props: IDocumentInAppViewProps): JSX.Element {
    const { getText } = useTranslation();
    const [, setFileAnnotations, fileAnnotationsRef] = useStateRef(null);

    const [patientBannerModel, setPatientBannerModel] = useState<IPatientBannerModel>(null);
    const [isMedicalSummaryLoaded, setIsMedicalSummaryLoaded] = useState<boolean>(false);
    const [enableSignature, setEnableSignature] = useState<boolean>(false);
    const [faxModalOpen, setFaxModalOpen] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState(props.document.get("MedicalDocumentInfo").Model.DocumentPathList.firstOrDefault());
    const [hasNewAnnotations, setHasNewAnnotations] = useState<boolean>(false);
    const [revokeApprovalModalOpen, setRevokeApprovalModalOpen] = useState<boolean>(false);
    const [isDocumentCorrupted, setIsDocumentCorrupted] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [signatureFlowState, setSignatureFlowState] = useState<ISignatureFlowState>(() => ({
        canUserSign: sessionStore.hasPinSet && !!settingsStore.configuration.get("SignatureByteArray"),
        shouldSkipPinTokenValidation: false,
        shouldSkipToSetActionToSignature: false
    }));
    const [showReturnFaxToSenderPopup, setShowReturnFaxToSenderPopup] = useState<boolean>(false);

    const patient: IndividualLight = useMemo(
        () => props.document.get("MedicalDocumentInfo").Model.Patients.firstOrDefault(),
        [props.document]
    );

    const medicalDocumentItem: MedicalDocumentItem = useMemo(
        () => props.document.get("MedicalDocumentInfo"),
        [props.document]
    );

    const isSelectedFilePdf: boolean = useMemo(
        () => selectedFile.FileName.toUpperCase().endsWith(".PDF"),
        [selectedFile]
    );

    const displaySignature: boolean = useMemo(
        () => isSelectedFilePdf && signatureFlowState.canUserSign,
        [isSelectedFilePdf, signatureFlowState.canUserSign]
    );

    const editorRef = useRef<PdfAnnotationEditorRef>(null);
    const previousDocumentId = useRef<string>();

    const isClericalUser = sessionStore.getCurrentEmployee().isClerical();

    const hasNewAnnotationsRef = useRef<boolean>(hasNewAnnotations);

    useEffect(() => {
        hasNewAnnotationsRef.current = hasNewAnnotations;
    }, [hasNewAnnotations]);

    useEffect(
        () => () => {
            saveAnnotations();
        },
        []
    );

    useEffect(
        () => {
            // refresh dimensions according to patient banner
            if (props.isPatientContext) {
                const bannerHeight = document.querySelector(".myle-page-header")?.clientHeight;
                const totalHeight = `calc(100vh - ${226 + bannerHeight}px)`;
                document.getElementById("documentView").style.maxHeight = totalHeight;
                document.getElementById("documentView").style.height = totalHeight;
                $(window).trigger("resize");
            }
            // refresh patient banner data
            if (!patient?.Id) {
                setPatientBannerModel(null);
            }
            else {
                SecondScreenNoteService.getInstance().updateModel(new SecondScreenNoteData({ patientId: patient.Id }));
                loadPatientBannerInformation(patient.Id);
            }
        },
        [patient, props.isPatientContext]
    );

    useEffect(
        () => {
            // when document change, update selected file and cleanup annotations
            if (previousDocumentId.current !== medicalDocumentItem.Model.Id) {
                setSelectedFile(medicalDocumentItem.Model.DocumentPathList.firstOrDefault());
                setHasNewAnnotations(false);
                setIsDocumentCorrupted(false);
            }
            previousDocumentId.current = medicalDocumentItem.Model.Id;
        },
        [medicalDocumentItem]
    );

    async function loadPatientBannerInformation(patientId: string): Promise<void> {
        const patientBannerModel = await PatientBannerHelper.loadPatientBannerModel(
            patientId,
            null,
            true
        );

        setPatientBannerModel(patientBannerModel);
    }

    useEffect(
        () => {
            const validatePinFrequencyToken = async (): Promise<void> => {
                if (cacheStore.ClinicSettings.EnableQuickDocumentSigning && signatureFlowState.canUserSign && !signatureFlowState.shouldSkipPinTokenValidation) {
                    const currentUser = sessionStore.getCurrentEmployee().Model;
                    if (!currentUser?.SignatureByteArray) {
                        return;
                    }

                    const token = LoginPinFrequencyModel.fromResponseModel(currentUser.LoginPinFrequency).documentToken ?? "";
                    const isTokenValid = await ExportableFileHelper.validatePinFrequencyToken(token);

                    setSignatureFlowState(p => ({ ...p, shouldSkipToSetActionToSignature: isTokenValid }));
                    setEnableSignature(isTokenValid);
                }
            };

            validatePinFrequencyToken();
        },
        [signatureFlowState.canUserSign, signatureFlowState.shouldSkipPinTokenValidation]
    );

    async function handleNextDocument(navigation: DocumentNavigation): Promise<void> {
        await saveAnnotations();
        props.onDocumentNavigation(navigation);
    }

    async function onPortalStatusClick(): Promise<void> {
        const hasNewAnnotations = hasNewAnnotationsRef.current;
        await saveAnnotations(true);
        // We have to update directly the model here to get the first annotations made taken into account
        if (hasNewAnnotations)
            medicalDocumentItem.Model.IsAnnotated = true;

        sessionStore.dispatch(LocalOperations.Documents.PortalStatusClicked, { doc: medicalDocumentItem });
    }

    function openSecondScreen(): void {
        SecondScreenHelper.openWindow({ patientId: patient.Id });
    }

    async function onSave(): Promise<void> {
        if (!hasNewAnnotations)
            return;

        await saveAnnotations();

        redirectDocument();
    }

    function canApproveDocument(): boolean {
        return IsNullOrWhiteSpace(medicalDocumentItem.Model.SignOffByContactId) && sessionStore.canApproveDocument();
    }

    async function approveDocument(): Promise<void> {
        const response = await Client.postAsync<{ isSuccess: boolean, errors: string[] }>(
            ServerOperations.Server.Myle_MedicalDocuments_ApproveDocuments,
            {
                MedicalDocumentIds: [medicalDocumentItem.Model.Id]
            }
        );

        if (!response.isSuccess) {
            const errors = response.errors[0].Message?.split("\\n");
            sessionStore.showPopup(errors);
            return;
        }
        medicalDocumentItem.Model.SignOffByContactId = sessionStore.getCurrentEmployee().ContactId;
    }

    async function onApprove(): Promise<void> {
        await saveAnnotations();
        await approveDocument();
        redirectDocument();
    }

    function redirectDocument(): void {
        if (props.documentIndex + 1 < props.documentCount && cacheStore.UserSettings?.UserSettingsConfig?.ShowDocumentInAppView)
            props.onDocumentNavigation(DocumentNavigation.Forward);
        else
            onClose();
    }

    async function saveAnnotations(reload: boolean = false): Promise<void> {
        if (!hasNewAnnotationsRef.current || !fileAnnotationsRef?.current)
            return;

        setIsSaving(true);
        const response = await Client.postAsync(
            ServerOperations.Server.Myle_MedicalDocuments_SaveAnnotations,
            {
                FileAnnotations: fileAnnotationsRef.current
            }
        );
        setIsSaving(false);

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        if (reload) {
            await editorRef.current.reloadAnnotations();
        }

        setHasNewAnnotations(false);
    }

    function onClose(): void {
        sessionStore.dispatch(LocalOperations.Documents.ReturnToSearch);
    }

    function handleFaxSent(response: ClientResponse, onCompleted?: VoidFunction): void {
        setFaxModalOpen(false);
        if (response.isSuccess) {
            displayToast(MessageToast.Success, getText("FaxSentLabel"));
            redirectDocument();
        }
        else {
            displayToast(MessageToast.Error, response.errors.map(error => error.Message).join("\n -"));
        }
        onCompleted?.();
    }

    const portalButtonProps = PortalHelper.getPortalButtonPropsForShareableItem(medicalDocumentItem);

    async function sendFax(mergedFileName: string, faxDetails: InboundFaxDetails, onCompleted?: VoidFunction): Promise<void> {
        if (!mergedFileName) {
            console.error("The file name is undefined.");
            return;
        }

        if (!faxDetails.faxNumber) {
            console.error("The fax number is undefined.");
            return;
        }

        if (faxDetails.faxNumber && mergedFileName) {
            const { displayName, faxNumber, phoneNumber, pdfCoverPageParams } = faxDetails;

            const faxGenericPDFComposite = new FaxGenericPDFComposite();
            faxGenericPDFComposite.pdfFileName = mergedFileName;
            faxGenericPDFComposite.patientId = patient?.Id;
            faxGenericPDFComposite.doctorName = displayName;
            faxGenericPDFComposite.doctorFax = faxNumber;
            faxGenericPDFComposite.doctorPhone = phoneNumber;
            faxGenericPDFComposite.sentFaxItemTypeId = EnumSentFaxItemType.Document;
            const response = await Client.postAsync(
                ServerOperations.Server.Myle_Faxing_SendGenericPDF,
                {
                    FaxGenericPDFComposite: faxGenericPDFComposite,
                    PdfCoverPageParams: pdfCoverPageParams
                }
            );

            handleFaxSent(response, onCompleted);
        }
    }

    async function buildAndSendFax(selectedFiles: ExportableFile[], faxDetails: InboundFaxDetails, onCompleted?: VoidFunction): Promise<void> {
        await saveAnnotations();
        if (canApproveDocument())
            await approveDocument();

        const file = await ExportableFileHelper.mergeExportableFiles(
            selectedFiles,
            null,
            fileName => sendFax(fileName, faxDetails, onCompleted)
        );

        if (file?.getAttachedObject_ObjectId())
            sendFax(file.getAttachedObject_ObjectId(), faxDetails, onCompleted);
    }

    async function handleReturnToSender(faxDetails: InboundFaxDetails, onCompleted?: VoidFunction): Promise<void> {
        const selectedFiles = medicalDocumentItem.Model.DocumentPathList.map(file => ExportableFileHelper.fromExportableFileResponse(new ExportableFileResponse({
            Id: file.Id.toUpperCase(),
            Type: FileType.DOCUMENT,
            Date: file.CreationDate?.toString(),
            Description: file.OriginalFileName
        })));

        await buildAndSendFax(selectedFiles, faxDetails, onCompleted);
    }

    function onReturnToSenderButtonClick(): void {
        if (cacheStore.ClinicSettings.EnableFaxCoverSheet) {
            setShowReturnFaxToSenderPopup(true);
        }
        else {
            const faxSentBy = medicalDocumentItem.Model.SenderFaxSource ?? "-";
            const faxNumber = medicalDocumentItem.Model.SenderFaxNumber;

            handleReturnToSender({ displayName: faxSentBy, faxNumber });
        }
    }

    async function onRevokeApproval(): Promise<void> {
        await saveAnnotations();
        setRevokeApprovalModalOpen(false);
    }

    async function handleFaxModalOpen(): Promise<void> {
        await saveAnnotations(true);
        setFaxModalOpen(true);
    }

    async function refreshPinToken(pin: string): Promise<void> {
        const response = await SignatureClient.refreshPinFrequencyToken(PinFrequencyDocumentType.Document, pin);

        if (response) {
            sessionStore.dispatch(LocalOperations.Login.UpdatePinFrequency, {
                pinFrequencyDocumentType: response.SignaturePinType,
                token: response.Token
            });

            sessionStore.dispatch(LocalOperations.Settings.UpdatePinFrequency, {
                pinFrequencyDocumentType: response.SignaturePinType,
                token: response.Token
            });
        }
    }

    return (
        <div
            className={`${styles.root} ${(props.isPatientContext ? styles.patientContext : "")}`}
        >
            <PdfAnnotationEditor
                ref={editorRef}
                medicalDocumentItem={medicalDocumentItem.Model}
                selectedFileId={selectedFile.Id}
                onSelectedFileChange={fileId => {
                    setSelectedFile(medicalDocumentItem.Model.DocumentPathList.firstOrDefault(p => p.Id === fileId));
                    setIsDocumentCorrupted(false);
                }}
                displaySignature={displaySignature}
                enableSignature={enableSignature}
                isSelectedFilePdf={isSelectedFilePdf}
                onEditedStatusChanged={async isEdited => {
                    setHasNewAnnotations(isEdited);
                    if (isEdited) {
                        setFileAnnotations(await editorRef.current.getFileAnnotations());
                    }
                }}
                onSaveAnnotations={() => saveAnnotations(true)}
                onCorruptedFileDisplay={() => setIsDocumentCorrupted(true)}
                skipSettingActionToSignature={signatureFlowState.shouldSkipToSetActionToSignature}
            />
            {props.isSendToPortalPopupOpen && (
                <SendToPortalPopup
                    model={props.portalLinkedItem}
                    strategy={new SendDocumentStrategy()}
                />
            )}
            <div className={`${styles.patientInformation} patient-banner-form-wrapper`} data-cy="documentInApp-patientInformation">
                <div className={`${styles.patientVerticalBanner} myle-form`}>
                    {!props.isPatientContext && patientBannerModel && isMedicalSummaryLoaded && (
                        <PatientBannerVertical
                            model={patientBannerModel}
                            hideWaitingList={true}
                            hideRelatedRecalls={true}
                        />
                    )}
                    {!isClericalUser && (
                        <>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className={styles.medicalSummaryContainer}>
                                        <span className="graytext">{getText("CppLabel")}</span>
                                        {isMedicalSummaryLoaded &&
                                            (
                                                <SummaryPanelButtons
                                                    onPopoutClick={() => openSecondScreen()}
                                                    hideOpenSummaryPanelLink
                                                    cssClass="documentInAppViewContainer"
                                                />
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                            <MedicalSummaryPanel
                                patientContactId={patient?.Id}
                                context={props.isPatientContext
                                    ? MedicalSummaryPanelContext.PatientDocumentsTab
                                    : MedicalSummaryPanelContext.DocumentsTab
                                }
                                onMedicalSummaryLoaded={() => setIsMedicalSummaryLoaded(true)}
                            />
                        </>
                    )}
                </div>
            </div>
            <div className={styles.footer}>
                <div>
                    <button
                        className="btn btn-primary"
                        onClick={() => handleNextDocument(DocumentNavigation.Backward)}
                        disabled={props.documentIndex === 0}
                        children="<"
                    />
                    <button
                        className="btn btn-primary"
                        onClick={() => handleNextDocument(DocumentNavigation.Forward)}
                        disabled={props.documentIndex + 1 === props.documentCount}
                        children=">"
                    />
                    <>
                        {cacheStore.ClinicSettings.EnableQuickDocumentSigning && isSelectedFilePdf && !signatureFlowState.canUserSign && !isDocumentCorrupted && !enableSignature && (
                            <ButtonControl
                                label={getText("SignLabel")}
                                className="btn btn-default"
                                onClick={() => {
                                    sessionStore.dispatchMain(
                                        LocalOperations.MainComponentViewOperations.DisplaySignatureOptionPopup,
                                        {
                                            onSaveSuccess: () => {
                                                const canSign = sessionStore.hasPinSet && !!settingsStore.configuration.get("SignatureByteArray");

                                                setSignatureFlowState(() => ({
                                                    canUserSign: canSign,
                                                    shouldSkipPinTokenValidation: canSign,
                                                    shouldSkipToSetActionToSignature: canSign
                                                }));

                                                if (canSign) {
                                                    setEnableSignature(true);
                                                }
                                            }
                                        }
                                    );
                                }}
                            />
                        )}
                        {!enableSignature && displaySignature && !isDocumentCorrupted && (
                            <span className={styles.pin}>
                                <SignaturePinControl
                                    onSuccessFunction={async pin => {
                                        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                                            await refreshPinToken(pin);
                                        }
                                        setEnableSignature(true);
                                    }}
                                    onRejectFunction={() => setEnableSignature(false)}
                                    disableOnSuccess={true}
                                />
                            </span>
                        )}
                    </>
                    {PortalHelper.shouldShowPortalShareButton(medicalDocumentItem.Model.Patients) && (
                        <span>
                            <PortalStatusButton
                                icon="share"
                                onClick={() => onPortalStatusClick()}
                                {...portalButtonProps}
                            />
                        </span>
                    )}
                </div>
                <div data-cy="documentInApp-footer">
                    <button className="btn btn-default" onClick={onClose}>
                        {getText("CancelLabel")}
                    </button>
                    <button disabled={!hasNewAnnotations || isSaving} onClick={onSave} className="btn btn-primary">
                        {getText("SaveLabel")}
                    </button>
                    {!isDocumentCorrupted && (
                        <button disabled={isSaving} onClick={handleFaxModalOpen} className="btn btn-primary">
                            {getText("FaxVerbLabel")}
                        </button>
                    )}
                    {medicalDocumentItem.Model.SenderFaxNumber && (
                        <button onClick={() => onReturnToSenderButtonClick()} disabled={isSaving} className="btn btn-primary">
                            {getText("ReturnToSenderLabel")}
                        </button>
                    )}
                    {(canApproveDocument()) && !isDocumentCorrupted && (
                        <button disabled={isSaving} onClick={onApprove} className="btn btn-primary">
                            {getText("ApproveLabel")}
                        </button>
                    )}

                    {sessionStore.isAdmin() && !IsNullOrWhiteSpace(medicalDocumentItem.Model.SignOffByContactId) && (
                        <button
                            className="btn btn-danger"
                            style={{ "verticalAlign": "bottom" }}
                            onClick={() => setRevokeApprovalModalOpen(true)}
                            disabled={isSaving}
                        >
                            {getText("RevokeApprovalLabel")}
                        </button>
                    )}
                </div>
            </div>
            {faxModalOpen && patientBannerModel && (
                <DocumentFax
                    onClose={() => setFaxModalOpen(false)}
                    onFax={(selectedFiles, faxDetails, onCompleted) => buildAndSendFax(selectedFiles, faxDetails, onCompleted)}
                    patient={patientBannerModel.get("Model")}
                    selectedDocument={medicalDocumentItem.Model}
                />
            )}
            {revokeApprovalModalOpen && (
                <RevokeApprovalModal
                    onClose={() => setRevokeApprovalModalOpen(false)}
                    document={medicalDocumentItem}
                    onRevokeApproval={() => onRevokeApproval()}
                />
            )}
            {showReturnFaxToSenderPopup && (
                <OutboundFaxCreationPopup
                    onCancel={() => setShowReturnFaxToSenderPopup(false)}
                    onSubmit={(faxDetails, onCompleted) => handleReturnToSender(faxDetails, () => {
                        setShowReturnFaxToSenderPopup(false);
                        onCompleted?.();
                    })}
                    popupTitle={getText("ReturnToSenderTitleLabel")}
                    initialFaxNumber={medicalDocumentItem.Model.SenderFaxNumber ?? ""}
                    showX
                    returnFaxSender
                />
            )}
        </div>
    );
}

export { DocumentInAppView, DocumentNavigation };
